import { memo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DragDropProvider, useDragDropManager } from '@dnd-kit/react';
import { useAppDispatch, useAppSelector } from '../../../../../hooks/redux';
import {
  selectScheduleCanUndo,
  selectScheduleSelectedMatchIds,
  selectUnscheduledMatches,
} from '../../../../../features/schedule/scheduleSelectors';
import { selectScheduleConflictCount } from '../../../../../features/schedule/scheduleConflictSelectors';
import {
  addUnscheduledItemToSchedule,
  applyGroupScheduleDragAction,
  swapScheduleItems,
} from '../../../../../features/schedule/scheduleActions';
import { MIN_SCHEDULE_BULK_ADJUST } from '../../../../../features/schedule/scheduleSelectionConstants';
import { undoScheduleChange } from '../../../../../features/schedule/scheduleSlice';
import {
  IFreeSlotItem,
  IMatchScheduleItem,
  IScheduleConstraints,
  ITournamentSchedule,
  isFreeSlot,
} from '../../../../../types/schedule';
import type { Class } from '../../../../../types/tournament';
import { flattenScheduledMatches } from '../../../../../features/schedule/scheduleOverlap';
import { canCommitDragDrop } from '../../../../../features/schedule/scheduleDragDropValidation';
import type { ScheduleZoomFloors } from '../../../../../features/schedule/scheduleZoomBounds';
import { MatchEditModal } from './MatchEditModal';
import { ScheduleToolbarButton } from './ScheduleToolbarButton';
import { resolveDropTargetData } from './scheduleDnDHelpers';
import type { SchedulePlacementController } from './schedulePlacementState';
import { schedulePlacementPropsAreEqual } from './schedulePlacementEquality';
import { ScheduleGridHost } from './ScheduleGridHost';
import { ScheduleLeftRail, type ScheduleLeftRailTab } from './ScheduleLeftRail';
import type { ScheduleConflict } from '../../../../../features/schedule/scheduleConflictDetector';
import './ScheduleLeftRail.scss';

const DragOperationCleanup = ({
  pendingPlaceMatchId,
  onClear,
}: {
  pendingPlaceMatchId: number | null;
  onClear: () => void;
}) => {
  const manager = useDragDropManager();

  const cancelActiveDragOperation = useCallback(
    (event?: Event) => {
      if (!manager) return;
      const status = manager.dragOperation.status;
      if (!status.dragging && !status.initializing) return;
      //@ts-ignore
      manager.actions.stop({ event, canceled: true });
      onClear();
    },
    [manager, onClear]
  );

  useEffect(() => {
    if (!manager) return;

    const stopMissedDragEnd = (event: Event) => cancelActiveDragOperation(event);

    window.addEventListener('pointercancel', stopMissedDragEnd);
    window.addEventListener('blur', stopMissedDragEnd);
    return () => {
      window.removeEventListener('pointercancel', stopMissedDragEnd);
      window.removeEventListener('blur', stopMissedDragEnd);
    };
  }, [manager, cancelActiveDragOperation]);

  useEffect(() => {
    if (pendingPlaceMatchId == null) return;
    cancelActiveDragOperation();
  }, [pendingPlaceMatchId, cancelActiveDragOperation]);

  return null;
};

export type ScheduleWorkbenchProps = {
  selectedDayKey: number;
  onSelectedDayKeyChange: (dayKey: number) => void;
  scheduleMutationBlocked: boolean;
  placement: SchedulePlacementController;
  onDayZoomFloorsChange: (dayKey: number, floors: ScheduleZoomFloors) => void;
  isMobile: boolean;
  showScheduleConflicts: boolean;
  conflictMatchIdList: number[];
  blockInvalidPlacement: boolean;
  schedule: ITournamentSchedule | null;
  scheduleConstraints: IScheduleConstraints | null;
  scheduleTimeZone: string;
  classIdToObject: Record<string, Class>;
  leftRailTab: ScheduleLeftRailTab;
  onLeftRailTabChange: (tab: ScheduleLeftRailTab) => void;
  focusedConflictMatchId: number | null;
  focusedConflictPeerIds: number[];
  conflictFocusPulseGeneration: number;
  onFocusConflictOnGrid: (conflict: ScheduleConflict) => void;
  onViewConflictMatch: (matchId: number) => void;
};

export const ScheduleWorkbench = memo(function ScheduleWorkbench({
  selectedDayKey,
  onSelectedDayKeyChange,
  scheduleMutationBlocked,
  placement,
  onDayZoomFloorsChange,
  isMobile,
  showScheduleConflicts,
  conflictMatchIdList,
  blockInvalidPlacement,
  schedule,
  scheduleConstraints,
  scheduleTimeZone,
  classIdToObject,
  leftRailTab,
  onLeftRailTabChange,
  focusedConflictMatchId,
  focusedConflictPeerIds,
  conflictFocusPulseGeneration,
  onFocusConflictOnGrid,
  onViewConflictMatch,
}: ScheduleWorkbenchProps) {
  const { t } = useTranslation();
  const prefix = 'creativeDetail-';
  const dispatch = useAppDispatch();
  const unscheduledMatches = useAppSelector(selectUnscheduledMatches);
  const selectedMatchIds = useAppSelector(selectScheduleSelectedMatchIds);
  const conflictCount = useAppSelector(selectScheduleConflictCount);
  const canUndoSchedule = useAppSelector(selectScheduleCanUndo);

  const pendingPlaceMatch = placement.pendingPlaceMatch;
  const placeModeActive =
    placement.pendingPlaceMatchId != null && !scheduleMutationBlocked;

  const showLeftRail =
    !isMobile || unscheduledMatches.length > 0 || conflictCount > 0 || placeModeActive;

  return (
    // @ts-ignore DragDropProvider has children
    <DragDropProvider
      onDragStart={(event: any) => {
        if (scheduleMutationBlocked) return;
        if (event.nativeEvent?.shiftKey) return;
        const data = event.operation?.source?.data;
        if (!data || isFreeSlot(data)) return;
        const match = placement.resolveMatchFromDnDData(data);
        if (!match) return;
        const isGroupDrag =
          Boolean(match.scheduleDate) &&
          Boolean(match.court) &&
          selectedMatchIds.includes(match.id) &&
          selectedMatchIds.length >= MIN_SCHEDULE_BULK_ADJUST;
        placement.setDragPlacement({
          match,
          source: match.scheduleDate ? 'scheduled' : 'unscheduled',
          groupMatchIds: isGroupDrag ? selectedMatchIds : undefined,
        });
      }}
      onDragEnd={(event) => {
        const dragSource = placement.dragPlacement?.source ?? null;
        const groupMatchIds = placement.dragPlacement?.groupMatchIds;
        const draggedMatch = placement.dragPlacement?.match;
        placement.setDragPlacement(null);
        if (scheduleMutationBlocked) return;
        const { source } = event.operation;
        const targetData = resolveDropTargetData(event);
        const sourceMatch = source?.data
          ? placement.resolveMatchFromDnDData(source.data)
          : undefined;
        if (!event.canceled && sourceMatch && targetData) {
          const isGroupDrag =
            groupMatchIds &&
            groupMatchIds.length >= MIN_SCHEDULE_BULK_ADJUST &&
            draggedMatch &&
            groupMatchIds.includes(sourceMatch.id);

          if (isGroupDrag) {
            if (
              draggedMatch.scheduleDate &&
              draggedMatch.court &&
              isFreeSlot(targetData)
            ) {
              void dispatch(
                applyGroupScheduleDragAction(
                  groupMatchIds,
                  {
                    scheduleDate: draggedMatch.scheduleDate,
                    courtId: draggedMatch.court.id,
                  },
                  targetData as IFreeSlotItem,
                  t,
                  blockInvalidPlacement
                )
              );
            }
            return;
          }

          const sourceIsScheduled = Boolean(source?.data?.isScheduled);
          const allScheduledMatchesFlat = schedule
            ? flattenScheduledMatches(schedule)
            : [];
          const mayCommit =
            !blockInvalidPlacement ||
            !schedule ||
            !scheduleConstraints ||
            canCommitDragDrop({
              sourceMatch,
              sourceIsScheduled,
              targetData,
              schedule,
              constraints: scheduleConstraints,
              timeZone: scheduleTimeZone,
              classIdToObject,
              blockInvalidPlacement: true,
              allScheduledMatchesFlat,
              dragPlacementSource: dragSource,
              unscheduledMatches,
            });
          if (!mayCommit) return;
          if (sourceIsScheduled) {
            const targetMatch = placement.resolveMatchFromDnDData(targetData);
            if (targetMatch && !isFreeSlot(targetData) && targetMatch.id !== sourceMatch.id) {
              dispatch(swapScheduleItems(sourceMatch, targetMatch, t));
            } else if (isFreeSlot(targetData)) {
              dispatch(swapScheduleItems(sourceMatch, targetData as IFreeSlotItem, t));
            }
          } else {
            dispatch(addUnscheduledItemToSchedule(targetData as IFreeSlotItem, sourceMatch, t));
          }
        }
      }}
    >
      <DragOperationCleanup
        pendingPlaceMatchId={placement.pendingPlaceMatchId}
        onClear={() => placement.setDragPlacement(null)}
      />
      <MatchEditModal />
      <div
        className={`schedule-workbench ${placeModeActive ? 'schedule-workbench--place-mode' : ''}`}
      >
        {!isMobile && pendingPlaceMatch && !placement.placementBannerDismissed && (
          <div
            ref={placement.placementFloatRef}
            className="schedule-workbench__placement-float alert alert-info mb-0 py-2 px-3 shadow-sm"
            role="status"
            onPointerDownCapture={placement.handlePlacementBannerPointerDownCapture}
          >
            <div className="schedule-workbench__placement-float__text">
              <div className="small fw-semibold">
                {t(prefix + 'schedulePendingPlacement', {
                  match: pendingPlaceMatch.matchId || `#${pendingPlaceMatch.id}`,
                })}
              </div>
              <div className="small text-muted mt-1">
                {t(prefix + 'scheduleClickToPlaceHelper')}
              </div>
              <div className="small text-muted mt-1">
                {t(prefix + 'scheduleKeyboardPlaceHelper')}
              </div>
            </div>
            <ScheduleToolbarButton
              shrink
              disabled={!canUndoSchedule || scheduleMutationBlocked}
              aria-label={t(prefix + 'scheduleUndo')}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={() => dispatch(undoScheduleChange())}
            >
              {t(prefix + 'scheduleUndo')}
            </ScheduleToolbarButton>
            <ScheduleToolbarButton
              shrink
              onPointerDown={(e) => e.stopPropagation()}
              onClick={() => placement.setClickPlacement(null)}
            >
              {t(prefix + 'scheduleCancelSelection')}
            </ScheduleToolbarButton>
          </div>
        )}
        <ScheduleLeftRail
          activeTab={leftRailTab}
          onActiveTabChange={onLeftRailTabChange}
          unscheduledCount={unscheduledMatches.length}
          conflictCount={conflictCount}
          unscheduledMatches={unscheduledMatches}
          placement={placement}
          isMobile={isMobile}
          scheduleMutationBlocked={scheduleMutationBlocked}
          selectedDayKey={selectedDayKey}
          onSelectedDayKeyChange={onSelectedDayKeyChange}
          onFocusConflictOnGrid={onFocusConflictOnGrid}
          onViewConflictMatch={onViewConflictMatch}
          showRail={showLeftRail}
        />
        <ScheduleGridHost
          selectedDayKey={selectedDayKey}
          placement={placement}
          onDayZoomFloorsChange={onDayZoomFloorsChange}
          showScheduleConflicts={showScheduleConflicts}
          conflictMatchIdList={conflictMatchIdList}
          blockInvalidPlacement={blockInvalidPlacement}
          focusedConflictMatchId={focusedConflictMatchId}
          focusedConflictPeerIds={focusedConflictPeerIds}
          conflictFocusPulseGeneration={conflictFocusPulseGeneration}
        />
      </div>
      {isMobile && pendingPlaceMatch && (
        <div className="schedule-placement-bar">
          <span className="schedule-placement-bar__text">
            {t(prefix + 'scheduleCurrentlyPlacing', {
              match: pendingPlaceMatch.matchId || `#${pendingPlaceMatch.id}`,
            })}
          </span>
          <div className="d-flex gap-2 flex-shrink-0">
            <button
              type="button"
              className="btn btn-sm btn-light"
              disabled={!canUndoSchedule || scheduleMutationBlocked}
              aria-label={t(prefix + 'scheduleUndo')}
              onClick={() => dispatch(undoScheduleChange())}
            >
              {t(prefix + 'scheduleUndo')}
            </button>
            <button
              type="button"
              className="btn btn-sm btn-light"
              onClick={() => placement.setClickPlacement(null)}
            >
              {t(prefix + 'scheduleCancelSelection')}
            </button>
          </div>
        </div>
      )}
    </DragDropProvider>
  );
}, (prev, next) =>
  prev.selectedDayKey === next.selectedDayKey &&
  prev.onSelectedDayKeyChange === next.onSelectedDayKeyChange &&
  prev.isMobile === next.isMobile &&
  prev.scheduleMutationBlocked === next.scheduleMutationBlocked &&
  prev.showScheduleConflicts === next.showScheduleConflicts &&
  prev.conflictMatchIdList === next.conflictMatchIdList &&
  prev.blockInvalidPlacement === next.blockInvalidPlacement &&
  prev.schedule === next.schedule &&
  prev.scheduleConstraints === next.scheduleConstraints &&
  prev.scheduleTimeZone === next.scheduleTimeZone &&
  prev.classIdToObject === next.classIdToObject &&
  prev.leftRailTab === next.leftRailTab &&
  prev.onLeftRailTabChange === next.onLeftRailTabChange &&
  prev.onDayZoomFloorsChange === next.onDayZoomFloorsChange &&
  prev.focusedConflictMatchId === next.focusedConflictMatchId &&
  prev.focusedConflictPeerIds === next.focusedConflictPeerIds &&
  prev.conflictFocusPulseGeneration === next.conflictFocusPulseGeneration &&
  prev.onFocusConflictOnGrid === next.onFocusConflictOnGrid &&
  prev.onViewConflictMatch === next.onViewConflictMatch &&
  schedulePlacementPropsAreEqual(prev.placement, next.placement));
